import * as styles from "./DialogContent.css";

import { clsx } from "clsx";
import { type JSX, useCallback, useMemo, useRef } from "react";

import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import {
  ShortcutScopeContext,
  useShortcutScope,
} from "@frontend/hooks/useScopedShortcut";
import { resolveSize } from "@frontend/styles";
import { mergeRefs } from "@frontend/utils/mergeRefs";
import { growFromOrigin } from "@frontend/utils/transitions";

import { useDialogAnimationContext } from "./Dialog.context";
import type { DialogContentProps } from "./Dialog.types";
import { DialogBackdrop } from "./DialogBackdrop";
import { DialogPortal } from "./DialogPortal";
import { DIALOG_SIZES } from "./constants";

export function DialogContent({
  className,
  children,
  size = "md",
  placement = "center",
  transition = growFromOrigin,
  backdrop = true,
  fullscreen = false,
  style,
  ...props
}: DialogContentProps): JSX.Element {
  const originRef = useDialogAnimationContext();

  const { variant: sizeVariant, style: widthVar } = resolveSize(
    size,
    DIALOG_SIZES,
    styles.customWidth,
  );

  const sizeStyle =
    !widthVar || fullscreen
      ? style
      : typeof style === "function"
        ? (state: Parameters<typeof style>[0]) => ({
            ...widthVar,
            ...style(state),
          })
        : { ...widthVar, ...style };

  const observerRef = useRef<MutationObserver | null>(null);

  const openRef = useRef(false);

  const popupCallbackRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) {
        observerRef.current?.disconnect();
        observerRef.current = null;
        return;
      }

      const enter = () => {
        if (openRef.current) {
          return;
        }

        openRef.current = true;
        transition.enter(el, originRef.current);
      };

      const exit = () => {
        if (!openRef.current) {
          return;
        }

        openRef.current = false;

        transition.exit(el, originRef.current);

        originRef.current = null;
      };

      enter();

      const observer = new MutationObserver(() => {
        if (el.hasAttribute("data-closed")) {
          exit();

          return;
        }

        // Reopened without remounting: the exit animation left it invisible.
        enter();
      });

      observer.observe(el, {
        attributes: true,
        attributeFilter: ["data-closed"],
      });

      observerRef.current = observer;
    },
    [originRef, transition],
  );

  const { scope, ref: scopeRef } = useShortcutScope({ modal: true });

  const popupRef = useMemo(
    () => mergeRefs(popupCallbackRef, scopeRef),
    [popupCallbackRef, scopeRef],
  );

  return (
    <DialogPortal>
      {backdrop && <DialogBackdrop />}
      <BaseDialog.Popup
        ref={popupRef}
        data-slot="dialog-content"
        className={clsx(
          styles.popup({ size: sizeVariant, placement, fullscreen }),
          className,
        )}
        style={sizeStyle}
        {...props}
      >
        <ShortcutScopeContext value={scope}>{children}</ShortcutScopeContext>
      </BaseDialog.Popup>
    </DialogPortal>
  );
}
